'use client';

/**
 * Root-layout error boundary — catches render-phase errors that escape the
 * tenant tree's `[tenant]/error.tsx` boundary (e.g. crashes in
 * `src/app/layout.tsx` itself, or routes outside `[tenant]/`). Next.js App
 * Router requires this file to render its own <html>/<body> since it
 * replaces the root layout.
 *
 * Renders the canonical user-facing copy from `@auctogon/core/api` with no
 * reference ID — pure-client errors don't carry a server requestId to
 * surface.
 *
 * Twin of `apps/admin-web/src/app/global-error.tsx` (per the
 * no-cross-app-copy-paste carve-out for app-shell files). See
 * `.claude/rules/error-handling-chain.md` for the bubble + middleware
 * pattern.
 */
import { useEffect } from 'react';
import { formatErrorMessage } from '@auctogon/core/api';

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        // Client-side only: the boot-time console patch is server-only (bails
        // when `window` is defined). Browser RUM was removed (BL-1267 / ADR
        // 043), so this render-phase error reaches the browser console only —
        // no telemetry backend. Server-side crashes still surface via the
        // `error.digest` and the Cloud Trace span from `apiHandler`; the
        // browser-side gap is only for render-phase failures of the root
        // layout itself.
        console.error('[Global Error Boundary]', error);
    }, [error]);

    return (
        <html lang="en">
            <body>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        gap: '1rem',
                        padding: '2rem',
                        fontFamily:
                            'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        color: '#111827',
                    }}
                >
                    <div
                        style={{
                            width: '3.5rem',
                            height: '3.5rem',
                            borderRadius: '9999px',
                            background: '#fef2f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem',
                        }}
                        aria-hidden="true"
                    >
                        ⚠
                    </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
                        Something went wrong
                    </h2>
                    <p
                        style={{
                            fontSize: '0.875rem',
                            color: '#4b5563',
                            textAlign: 'center',
                            maxWidth: '28rem',
                            margin: 0,
                        }}
                    >
                        {formatErrorMessage()}
                    </p>
                    <button
                        type="button"
                        onClick={reset}
                        style={{
                            // eslint-disable-next-line brandhex/no-hardcoded-brand-hex -- global-error renders standalone (no globals.css / Tailwind guaranteed), so the Auctogon-default chrome must be inline literals
                            background: '#09514d',
                            color: '#fff',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.5rem',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                        }}
                    >
                        Try again
                    </button>
                </div>
            </body>
        </html>
    );
}
